// @ts-nocheck
/**
 * CoachingService — Bridge événementiel entre LiveTranscript (SSE segments)
 * et le Live Coach panel dans CallSheet.
 *
 * Maintient, par call :
 *  - transcript : historique complet des segments (agent + prospect, ordonnes)
 *  - coachHistory : ses propres conseils precedents (pour eviter de se repeter
 *    et pour enchainer une vraie ligne de coaching)
 *
 * A chaque nouvelle replique prospect, on envoie au backend :
 *  - tout le transcript (trim aux N derniers segments pour controler les tokens)
 *  - la liste des conseils deja donnes
 *  - le contexte prospect (nom/titre/societe)
 *
 * Le backend peut ainsi coacher en contexte (ton de l'appel, objections deja
 * rencontrees, ses conseils precedents) et ne pas se repeter.
 */

import { api } from '@/api/api-client';

const MAX_TRANSCRIPT_SEGMENTS = 30;  // ~5-7 minutes d'appel, largement suffisant
const MAX_COACH_HISTORY = 10;         // les 10 derniers conseils

const MAX_RECENT_SUGGESTIONS = 3; // panel affiche les 3 dernieres (plus recente en haut)

export function createCoachingService() {
  const state = {
    callId: null,
    mode: 'idle',               // 'idle' | 'suggestion' | 'objection'
    suggestion: null,
    objection: null,
    lastProspectLine: null,
    active: true,
    // Pile des dernieres suggestions affichees (la plus recente en [0]).
    // Distinct de coachHistoryByCall (qui sert au prompt LLM, capped a 10).
    recentSuggestions: [], // Array<{ id, text, type, atTimestamp, objectionLabel? }>
    // AUDIT-ADM-07: the server answers an error status when the coach cannot
    // work (no model configured, model down). True until the next answer.
    unavailable: false,
  };
  // Historique par call — purge a chaque nouveau call via resetForCall
  const transcriptByCall = new Map();   // callId → Array<{speaker, text, timestamp}>
  const coachHistoryByCall = new Map(); // callId → Array<{type, text, objection?, atTimestamp}>

  const listeners = new Set();
  const seenChunks = new Set();
  let inFlight = false;

  function notify() {
    const snapshot = { ...state };
    for (const cb of listeners) {
      try { cb(snapshot); } catch { /* ignore */ }
    }
  }

  function subscribe(cb) {
    listeners.add(cb);
    cb({ ...state });
    return () => listeners.delete(cb);
  }

  function setActive(next) {
    state.active = !!next;
    notify();
  }

  function resetForCall(callId) {
    state.callId = callId || null;
    state.mode = 'idle';
    state.suggestion = null;
    state.objection = null;
    state.lastProspectLine = null;
    state.recentSuggestions = [];
    if (callId) {
      transcriptByCall.set(callId, []);
      coachHistoryByCall.set(callId, []);
    }
    notify();
  }

  // Pousse une nouvelle entree dans la pile recente. La plus recente est en [0].
  // Dedup sur le texte exact pour eviter qu'une meme suggestion fasse 3 lignes
  // identiques en cas de chunks proches sur le meme silence prospect.
  function pushRecent(entry) {
    if (!entry || !entry.text) return;
    const existing = state.recentSuggestions;
    if (existing[0] && existing[0].text === entry.text) return; // ignore exact duplicate consecutif
    state.recentSuggestions = [entry, ...existing].slice(0, MAX_RECENT_SUGGESTIONS);
  }

  /**
   * Pousse TOUT nouveau segment transcript (agent OU prospect) dans l'historique.
   * Appele par LiveTranscript pour chaque segment SSE recu.
   */
  function appendSegment(callId, segment) {
    if (!callId || !segment) return;
    if (!transcriptByCall.has(callId)) transcriptByCall.set(callId, []);
    const list = transcriptByCall.get(callId);
    list.push({
      speaker: segment.speaker,
      text: segment.text,
      timestamp: segment.timestamp,
    });
    // Cap pour ne pas croitre indefiniment en memoire (appels tres longs)
    if (list.length > MAX_TRANSCRIPT_SEGMENTS * 3) {
      list.splice(0, list.length - MAX_TRANSCRIPT_SEGMENTS * 3);
    }
  }

  /**
   * Declenche le coach sur la derniere replique prospect.
   * Envoie au backend : transcript complet (trime) + historique conseils + contexte.
   */
  async function submitProspectLine(callId, text, context) {
    if (!state.active) { console.warn('[Coach] submitProspectLine BAIL active=false', { callId, text: text?.slice(0, 40) }); return; }
    if (!callId || !text) { console.warn('[Coach] submitProspectLine BAIL missing', { callId, hasText: !!text }); return; }
    if (state.callId !== callId) { console.log('[Coach] resetForCall (callId changed)', { from: state.callId, to: callId }); resetForCall(callId); }
    const dedupKey = `${callId}::${text}`;
    if (seenChunks.has(dedupKey)) { console.log('[Coach] dedup skip (already seen)', text.slice(0, 40)); return; }
    seenChunks.add(dedupKey);
    state.lastProspectLine = text;
    notify();
    if (inFlight) { console.log('[Coach] inFlight skip (request already pending)'); return; }
    inFlight = true;
    const t0 = Date.now();
    try {
      const fullTranscript = transcriptByCall.get(callId) || [];
      const trimmedTranscript = fullTranscript.slice(-MAX_TRANSCRIPT_SEGMENTS);
      const coachHistory = (coachHistoryByCall.get(callId) || []).slice(-MAX_COACH_HISTORY);
      console.log('[Coach] → POST /ai/live-coach', { callId, chunk: text.slice(0, 60), transcriptSegments: trimmedTranscript.length, historyLen: coachHistory.length });
      const envelope = await api.functions.invoke('liveCoach', {
        callId,
        callSid: callId,
        transcriptChunk: text,
        // Nouveaux champs : contexte complet pour un coaching pertinent
        transcript: trimmedTranscript,
        coachHistory,
        context: context || {},
      });
      // api.functions.invoke renvoie toujours { data, status } — la payload
      // coach (type / text / suggestion) est dans envelope.data, PAS au niveau
      // racine. Sans ce unwrap, result.type === undefined → mode reste 'idle'
      // et le panel affiche "En écoute…" alors que le serveur a bien renvoyé
      // une suggestion (constate dans les logs prod : "OUT type=suggestion").
      const result = envelope?.data ?? envelope;
      console.log('[Coach] ← /ai/live-coach response', { ms: Date.now() - t0, status: envelope?.status, result });
      if (!result) { console.warn('[Coach] response empty/null'); return; }
      state.unavailable = false;
      const historyEntry = { atTimestamp: Date.now() };
      if (result.type === 'objection') {
        state.mode = 'objection';
        state.objection = { label: result.text, confidence: result.confidence };
        state.suggestion = result.suggestion || null;
        historyEntry.type = 'objection';
        historyEntry.objection = result.text;
        historyEntry.text = result.suggestion || '';
        pushRecent({ id: `${callId}-${Date.now()}`, type: 'objection', text: result.suggestion || result.text, objectionLabel: result.text, atTimestamp: Date.now() });
        console.log('[Coach] → state.mode=objection', { label: result.text, suggestion: result.suggestion?.slice(0, 80) });
      } else if (result.type === 'suggestion') {
        state.mode = 'suggestion';
        state.suggestion = result.text;
        state.objection = null;
        historyEntry.type = 'suggestion';
        historyEntry.text = result.text;
        pushRecent({ id: `${callId}-${Date.now()}`, type: 'suggestion', text: result.text, atTimestamp: Date.now() });
        console.log('[Coach] → state.mode=suggestion', { text: result.text?.slice(0, 80) });
      } else {
        state.mode = 'idle';
        historyEntry.type = 'idle';
        historyEntry.text = '';
        console.log('[Coach] → state.mode=idle (no actionable suggestion)');
      }
      // Memoriser le conseil seulement si non-idle (evite bruit historique)
      if (historyEntry.type !== 'idle') {
        if (!coachHistoryByCall.has(callId)) coachHistoryByCall.set(callId, []);
        coachHistoryByCall.get(callId).push(historyEntry);
      }
      console.log('[Coach] notify listeners count=', listeners.size);
      notify();
    } catch (e) {
      console.error('[Coach] FAIL', { callId, ms: Date.now() - t0, err: e?.message });
      state.unavailable = true;
      notify();
    } finally {
      inFlight = false;
    }
  }

  function clearCallState() {
    const oldCallId = state.callId;
    state.callId = null;
    state.mode = 'idle';
    state.recentSuggestions = [];
    notify();
    seenChunks.clear();
    // Purge les historiques du call termine (liberation memoire)
    if (oldCallId) {
      transcriptByCall.delete(oldCallId);
      coachHistoryByCall.delete(oldCallId);
    }
  }

  return {
    subscribe,
    setActive,
    appendSegment,         // <-- nouveau : appele pour CHAQUE segment (agent + prospect)
    submitProspectLine,
    clearCallState,
    resetForCall,
  };
}
